/** Analytics events (AP-1/AP-17): batched and delivered without blocking user flows. */
interface AnalyticsEvent {
  name: string;
  props?: Record<string, unknown>;
}

const pendingEvents: AnalyticsEvent[] = [];
const BATCH_SIZE = 50;
const MAX_EVENT_NAME_LENGTH = 120;
const MAX_PATH_LENGTH = 2048;
// Browsers cap the aggregate body of keepalive/Beacon requests at roughly
// 64 KiB. Leave headroom for implementation-specific bookkeeping.
const MAX_BATCH_BYTES = 60 * 1024;
const FLUSH_DELAY_MS = 3000;
const MAX_RETRY_DELAY_MS = 30_000;
let timer: number | undefined;
let inFlight = false;
let retryDelayMs = FLUSH_DELAY_MS;

function endpoint(): string {
  const base = import.meta.env.VITE_API_BASE_URL ?? '';
  return `${base}/api/v1/analytics/events`;
}

function scheduleFlush(delay = FLUSH_DELAY_MS): void {
  if (timer !== undefined || typeof window === 'undefined') return;
  timer = window.setTimeout(() => {
    timer = undefined;
    void flush();
  }, delay);
}

function byteLength(value: string): number {
  return new TextEncoder().encode(value).byteLength;
}

function buildBatch(): { events: AnalyticsEvent[]; body: string } {
  const events: AnalyticsEvent[] = [];
  let body = JSON.stringify({ events });

  for (const event of pendingEvents.slice(0, BATCH_SIZE)) {
    const candidate = [...events, event];
    const candidateBody = JSON.stringify({ events: candidate });
    if (byteLength(candidateBody) > MAX_BATCH_BYTES) {
      if (events.length > 0) break;

      // A single pathological event must not poison the FIFO forever. Preserve
      // its name and emission metadata, but replace oversized custom props.
      const compactEvent: AnalyticsEvent = {
        name: event.name.slice(0, MAX_EVENT_NAME_LENGTH),
        props: {
          truncated: true,
          ts: event.props?.ts,
          path: event.props?.path,
        },
      };
      events.push(compactEvent);
      body = JSON.stringify({ events });
      if (byteLength(body) > MAX_BATCH_BYTES) {
        events.length = 0;
        events.push({ name: 'analytics_event_truncated', props: { truncated: true } });
        body = JSON.stringify({ events });
      }
      break;
    }
    events.push(event);
    body = candidateBody;
  }

  return { events, body };
}

function isTransientStatus(status: number): boolean {
  return status === 408 || status === 429 || status >= 500;
}

export function track(name: string, props?: Record<string, unknown>) {
  pendingEvents.push({
    name: name.slice(0, MAX_EVENT_NAME_LENGTH),
    props: {
      ...props,
      ts: new Date().toISOString(),
      path: location.pathname.slice(0, MAX_PATH_LENGTH),
    },
  });
  scheduleFlush();
}

export async function flush(): Promise<void> {
  if (timer !== undefined) {
    window.clearTimeout(timer);
    timer = undefined;
  }
  if (inFlight || pendingEvents.length === 0) return;

  const { events: batch, body } = buildBatch();
  pendingEvents.splice(0, batch.length);
  inFlight = true;
  try {
    const response = await fetch(endpoint(), {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body,
      credentials: 'omit',
      keepalive: true,
    });
    if (!response.ok) {
      if (isTransientStatus(response.status)) {
        throw new Error(`analytics transport returned ${response.status}`);
      }
      // A permanent contract/auth rejection must not block every later event.
      retryDelayMs = FLUSH_DELAY_MS;
      return;
    }
    retryDelayMs = FLUSH_DELAY_MS;
  } catch {
    // Preserve ordering and retry later. Analytics remains best-effort, but a
    // temporary network/server failure must not delete events immediately.
    pendingEvents.unshift(...batch);
    retryDelayMs = Math.min(retryDelayMs * 2, MAX_RETRY_DELAY_MS);
    scheduleFlush(retryDelayMs);
  } finally {
    inFlight = false;
    if (pendingEvents.length > 0 && timer === undefined) scheduleFlush();
  }
}

function isSameOrigin(url: string): boolean {
  return new URL(url, window.location.href).origin === window.location.origin;
}

function flushOnPageHide(): void {
  if (pendingEvents.length === 0) return;

  const url = endpoint();
  const { events: batch, body } = buildBatch();
  // Beacon is the most reliable page-exit transport, but only use it for the
  // normal same-origin deployment. Cross-origin Beacon requests include
  // credentials and conflict with this API's credential-free CORS contract.
  if (
    isSameOrigin(url) &&
    typeof navigator.sendBeacon === 'function' &&
    navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }))
  ) {
    pendingEvents.splice(0, batch.length);
    if (pendingEvents.length === 0 && timer !== undefined) {
      window.clearTimeout(timer);
      timer = undefined;
    }
    return;
  }

  // This queued tail is separate from any batch already in flight. Dispatch it
  // independently so a page exit cannot strand events recorded during that
  // earlier request. Timestamps preserve logical ordering in PostHog.
  pendingEvents.splice(0, batch.length);
  void fetch(url, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body,
    credentials: 'omit',
    keepalive: true,
  })
    .then((response) => {
      if (!response.ok && isTransientStatus(response.status)) throw new Error('transient failure');
    })
    .catch(() => {
      pendingEvents.unshift(...batch);
      scheduleFlush(retryDelayMs);
    });
}

if (typeof window !== 'undefined') window.addEventListener('pagehide', flushOnPageHide);
